import React, { useState, useRef } from 'react';

const PETSTORE_SAMPLE = `openapi: 3.0.0
info:
  title: Petstore API
  version: 1.0.0
  description: A sample API that demonstrates APIScribe features.
servers:
  - url: https://petstore3.swagger.io/api/v3
    description: Petstore demo server
paths:
  /pet:
    post:
      tags: [pet]
      summary: Add a new pet
      requestBody:
        required: true
        content:
          application/json:
            schema:
              type: object
              properties:
                name: { type: string, example: doggie }
                status: { type: string, enum: [available, pending, sold] }
      responses:
        '200': { description: Successful operation }
        '405': { description: Invalid input }
  /pet/{petId}:
    get:
      tags: [pet]
      summary: Find pet by ID
      parameters:
        - name: petId
          in: path
          required: true
          schema: { type: integer }
          description: ID of pet to return
      responses:
        '200':
          description: Successful operation
          content:
            application/json:
              schema:
                type: object
                properties:
                  id: { type: integer }
                  name: { type: string }
                  status: { type: string }
        '404': { description: Pet not found }
    delete:
      tags: [pet]
      summary: Delete a pet
      parameters:
        - name: petId
          in: path
          required: true
          schema: { type: integer }
      responses:
        '200': { description: Successful operation }
        '400': { description: Invalid pet value }
  /pet/findByStatus:
    get:
      tags: [pet]
      summary: Find pets by status
      parameters:
        - name: status
          in: query
          schema: { type: string, enum: [available, pending, sold] }
      responses:
        '200': { description: Successful operation }
        '400': { description: Invalid status }
  /store/inventory:
    get:
      tags: [store]
      summary: Returns pet inventories by status
      responses:
        '200':
          description: Successful operation
          content:
            application/json:
              schema:
                type: object
                additionalProperties: { type: integer }
  /user/login:
    get:
      tags: [user]
      summary: Logs user into the system
      parameters:
        - name: username
          in: query
          required: true
          schema: { type: string }
        - name: password
          in: query
          required: true
          schema: { type: string }
      responses:
        '200': { description: Successful operation }
        '400': { description: Invalid credentials }
`;

const s = {
  label: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: '10px',
  },
  eyebrow: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    letterSpacing: '0.08em', textTransform: 'uppercase',
    color: 'var(--muted-foreground)',
  },
  sampleBtn: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    padding: '3px 10px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'var(--secondary)', color: 'var(--secondary-foreground)',
    cursor: 'pointer', letterSpacing: '0.02em',
    transition: 'background 0.15s',
  },
  dropzone: {
    borderRadius: 'var(--radius)', overflow: 'hidden',
    border: '1px solid var(--border)', transition: 'border-color 0.15s',
  },
  textarea: {
    width: '100%', minHeight: '200px', resize: 'vertical',
    fontFamily: '"Geist Mono", monospace', fontSize: '12px', lineHeight: 1.65,
    background: 'var(--card)', color: 'var(--card-foreground)',
    border: 'none', outline: 'none', padding: '14px 16px', display: 'block',
  },
  footer: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '8px 14px',
    background: 'var(--muted)', borderTop: '1px solid var(--border)',
  },
  footerMeta: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    color: 'var(--muted-foreground)',
  },
  uploadBtn: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    background: 'none', border: 'none',
    color: 'var(--muted-foreground)', cursor: 'pointer',
    textDecoration: 'underline',
  },
  clearBtn: {
    fontFamily: '"Geist Mono", monospace', fontSize: '11px',
    background: 'none', border: 'none',
    color: 'var(--muted-foreground)', cursor: 'pointer',
    marginLeft: 'auto',
  },
  errorBox: {
    background: 'oklch(from var(--destructive) l c h / 0.08)',
    border: '1px solid oklch(from var(--destructive) l c h / 0.3)',
    borderRadius: 'var(--radius)', padding: '10px 14px',
    fontFamily: '"Geist Mono", monospace', fontSize: '12px',
    color: 'var(--destructive)', lineHeight: 1.6,
  },
  analyzeBtn: {
    fontFamily: 'Geist, sans-serif', fontWeight: 500, fontSize: '13px',
    padding: '9px 22px', borderRadius: 'var(--radius)',
    border: '1px solid var(--border)',
    background: 'var(--primary)', color: 'var(--primary-foreground)',
    cursor: 'pointer', letterSpacing: '-0.01em',
    transition: 'opacity 0.15s',
  },
};

export default function SpecInput({ onParse, error }) {
  const [value, setValue] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const handleFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => setValue(e.target.result);
    reader.readAsText(file);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {/* Label row */}
      <div style={s.label}>
        <span style={s.eyebrow}>01 — Paste spec or upload file</span>
        <button style={s.sampleBtn} onClick={() => setValue(PETSTORE_SAMPLE)}>
          Load sample ↗
        </button>
      </div>

      {/* Drop zone */}
      <div
        style={{
          ...s.dropzone,
          borderColor: dragging ? 'var(--ring)' : error ? 'var(--destructive)' : 'var(--border)',
        }}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => {
          e.preventDefault(); setDragging(false);
          const f = e.dataTransfer.files[0];
          if (f) handleFile(f);
        }}
      >
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={'Paste OpenAPI JSON or YAML here…\n\nor drag & drop a .json / .yaml / .yml file'}
          style={s.textarea}
          spellCheck={false}
        />
        <div style={s.footer}>
          <span style={s.footerMeta}>
            {value.length > 0 ? `${value.length.toLocaleString()} chars` : '0 chars'}
          </span>
          <span style={s.footerMeta}>—</span>
          <button style={s.uploadBtn} onClick={() => fileRef.current?.click()}>
            upload .json / .yaml
          </button>
          <input
            ref={fileRef} type="file" accept=".json,.yaml,.yml"
            style={{ display: 'none' }}
            onChange={(e) => { if (e.target.files[0]) handleFile(e.target.files[0]); }}
          />
          {value && (
            <button style={s.clearBtn} onClick={() => setValue('')}>Clear</button>
          )}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={s.errorBox}>✗ {error}</div>
      )}

      {/* Submit */}
      <button style={s.analyzeBtn} onClick={() => onParse(value)}>
        Analyze →
      </button>
    </div>
  );
}
